import { conflicts } from '../data/conflicts';
import {
  categoryLabel,
  categoryColor,
  formatDeaths,
  formatNumber,
} from '../utils';
import type { ConflictCategory } from '../types';
import { glossarySpan } from '../tooltip';

const typeColors: Record<string, string> = {
  'state-based': '#3b82f6',
  'non-state': '#8b5cf6',
  'one-sided': '#ec4899',
  'mixed': '#f59e0b',
};

const typeLabels: Record<string, string> = {
  'state-based': 'State-based',
  'non-state': 'Non-state',
  'one-sided': 'One-sided',
  'mixed': 'Mixed',
};

export function renderTypesView(container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  // By type
  const types = ['state-based', 'non-state', 'mixed', 'one-sided'] as const;
  const byType: Record<string, typeof conflicts> = {};
  for (const t of types) {
    byType[t] = conflicts.filter(c => c.type === t);
  }

  // By category
  const categories: ConflictCategory[] = ['major_war', 'minor_war', 'conflict', 'skirmish'];
  const byCategory: Record<string, typeof conflicts> = {};
  for (const cat of categories) {
    byCategory[cat] = conflicts.filter(c => c.category === cat);
  }

  // Donut chart data
  const total = conflicts.length;
  const typeData = types.map(t => ({
    type: t,
    count: byType[t].length,
    pct: ((byType[t].length / total) * 100).toFixed(1),
    deaths: byType[t].reduce((s, c) => s + c.deaths2025, 0),
  }));

  // SVG donut
  const cx = 120, cy = 120, r = 90, r2 = 55;
  let startAngle = -Math.PI / 2;
  const arcs = typeData.map(d => {
    const angle = (d.count / total) * Math.PI * 2;
    const endAngle = startAngle + angle;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const x3 = cx + r2 * Math.cos(endAngle);
    const y3 = cy + r2 * Math.sin(endAngle);
    const x4 = cx + r2 * Math.cos(startAngle);
    const y4 = cy + r2 * Math.sin(startAngle);
    const large = angle > Math.PI ? 1 : 0;
    const path = `M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${r2},${r2} 0 ${large} 0 ${x4},${y4} Z`;
    startAngle = endAngle;
    return { ...d, path };
  });

  // Category bar chart
  const maxCatCount = Math.max(...categories.map(c => byCategory[c].length));

  wrapper.innerHTML = `
    <div class="view-header">
      <h2>Conflict Types & Categories</h2>
      <p>Breakdown by ${glossarySpan('State-based conflict', 'conflict type')} and ${glossarySpan('Intensity level', 'intensity category')}.</p>
    </div>

    <div class="chart-grid">
      <div class="chart-panel">
        <h3>By Conflict Type</h3>
        <p class="chart-subtitle">How conflicts are classified by the parties involved</p>
        <div style="display:flex;align-items:center;gap:var(--space-2xl);">
          <svg width="240" height="240" viewBox="0 0 240 240">
            ${arcs.map(a => `
              <path d="${a.path}" fill="${typeColors[a.type]}" opacity="0.8" class="bar-rect">
                <title>${typeLabels[a.type]}: ${a.count} conflicts (${a.pct}%)</title>
              </path>
            `).join('')}
            <text x="${cx}" y="${cy - 8}" text-anchor="middle" fill="var(--text-primary)" font-size="24" font-weight="700" font-family="var(--font-mono)">${total}</text>
            <text x="${cx}" y="${cy + 12}" text-anchor="middle" fill="var(--text-tertiary)" font-size="11">conflicts</text>
          </svg>
          <div>
            ${typeData.map(d => `
              <div style="display:flex;align-items:center;gap:8px;padding:6px 0;">
                <div style="width:12px;height:12px;border-radius:2px;background:${typeColors[d.type]}"></div>
                <div>
                  <div style="font-weight:600;font-size:var(--font-size-sm);">${typeLabels[d.type]} <span class="mono" style="color:var(--text-tertiary)">(${d.count})</span></div>
                  <div style="font-size:var(--font-size-xs);color:var(--text-tertiary)">~${formatDeaths(d.deaths)} fatalities (2025)</div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>

      <div class="chart-panel">
        <h3>By Intensity Category</h3>
        <p class="chart-subtitle">Classified by annual fatality count</p>
        <svg class="chart-svg" viewBox="0 0 600 ${categories.length * 50 + 20}" preserveAspectRatio="xMidYMid meet">
          ${categories.map((cat, i) => {
            const y = i * 50 + 10;
            const count = byCategory[cat].length;
            const w = (count / maxCatCount) * 400;
            const deaths = byCategory[cat].reduce((s, c) => s + c.deaths2025, 0);
            return `
              <g>
                <text x="130" y="${y + 20}" text-anchor="end" class="bar-label">${categoryLabel(cat)}</text>
                <rect class="bar-rect" x="140" y="${y + 4}" width="${w}" height="24" fill="${categoryColor(cat)}" rx="2" opacity="0.8">
                  <title>${categoryLabel(cat)}: ${count} conflicts, ~${formatNumber(deaths)} deaths (2025)</title>
                </rect>
                <text x="${140 + w + 8}" y="${y + 15}" class="bar-value" style="font-size:12px">${count}</text>
                <text x="${140 + w + 8}" y="${y + 28}" class="bar-label" style="font-size:9px;fill:var(--text-muted)">~${formatDeaths(deaths)} deaths</text>
              </g>
            `;
          }).join('')}
        </svg>
      </div>
    </div>

    <div style="margin-top: var(--space-2xl);">
      <div class="chart-panel">
        <h3>Type Definitions</h3>
        <p class="chart-subtitle">Understanding how conflicts are classified</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:var(--space-lg);margin-top:var(--space-md);">
          <div style="padding:var(--space-md);border-left:3px solid #3b82f6;">
            <div style="font-weight:600;color:#3b82f6;margin-bottom:4px;">${glossarySpan('State-based conflict')}</div>
            <div style="font-size:var(--font-size-sm);color:var(--text-secondary);">At least one party is a government. Includes civil wars and interstate wars.</div>
          </div>
          <div style="padding:var(--space-md);border-left:3px solid #8b5cf6;">
            <div style="font-weight:600;color:#8b5cf6;margin-bottom:4px;">${glossarySpan('Non-state conflict')}</div>
            <div style="font-size:var(--font-size-sm);color:var(--text-secondary);">Violence between organised groups where neither side is a government — gangs, militias, ethnic factions.</div>
          </div>
          <div style="padding:var(--space-md);border-left:3px solid #ec4899;">
            <div style="font-weight:600;color:#ec4899;margin-bottom:4px;">${glossarySpan('One-sided violence')}</div>
            <div style="font-size:var(--font-size-sm);color:var(--text-secondary);">Deliberate attacks on civilians by an organised actor. Includes massacres, terrorism, ethnic cleansing.</div>
          </div>
          <div style="padding:var(--space-md);border-left:3px solid #f59e0b;">
            <div style="font-weight:600;color:#f59e0b;margin-bottom:4px;">Mixed</div>
            <div style="font-size:var(--font-size-sm);color:var(--text-secondary);">Conflicts involving both state-based and non-state elements — e.g. a government fighting rebels while rebels also fight each other.</div>
          </div>
        </div>
      </div>
    </div>
  `;
}
